import React, { useEffect, useState } from "react";
import Switch from "react-switch";
import api from "../api.js";

// --- 2FA SETTINGS CARD ---
const TwoFactorSettings = ({ enabled, refreshStatus }) => {
  const [showQR, setShowQR] = useState(false);
  const [qrB64, setQrB64] = useState("");
  const [secret, setSecret] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [success, setSuccess] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Always log what you get from the parent (proves backend is sending it)
    console.log("2FA enabled prop from backend:", enabled);
  }, [enabled]);

  const handleToggle2FA = async (checked) => {
    setError("");
    setSuccess("");
    setCopied(false);

    if (checked) {
      if (enabled) return; // Already enabled
      setPending(true);
      try {
        const token = localStorage.getItem("token");
        const res = await api.post("/api/2fa/generate/", {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQrB64(res.data.qr_code_base64);
        setSecret(res.data.secret);
        setShowQR(true);
      } catch (e) {
        setError("Failed to generate 2FA. Maybe it's already enabled?");
      }
      setPending(false);
    } else {
      if (!enabled) return; // Already disabled
      if (!window.confirm("Are you sure you want to disable Two-Factor Authentication?")) return;
      setPending(true);
      try {
        const token = localStorage.getItem("token");
        await api.post("/api/2fa/disable/", {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setShowQR(false);
        setOtp("");
        setSecret("");
        setSuccess("2FA disabled!");
        if (typeof refreshStatus === "function") await refreshStatus();
      } catch (e) {
        setError("Failed to disable 2FA.");
      }
      setPending(false);
    }
  };

  const handleVerify2FA = async () => {
    setPending(true);
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("token");
      await api.post("/api/2fa/enable/", { otp_code: otp, secret }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowQR(false);
      setOtp("");
      setSecret("");
      setSuccess("2FA enabled!");
      if (typeof refreshStatus === "function") await refreshStatus();
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid 2FA code. Try again.");
    }
    setPending(false);
  };

  const handleCopySecret = () => {
    if (!secret) return;
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="card p-3 mb-4" style={{ marginBottom: "2rem" }}>
      <h3>Security Settings</h3>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <Switch
          checked={enabled || showQR}
          onChange={handleToggle2FA}
          disabled={pending || showQR}
          onColor="#4caf50"
          offColor="#ddd"
          checkedIcon={false}
          uncheckedIcon={false}
          height={22}
          width={46}
        />
        <span style={{ marginLeft: 16, fontSize: 16 }}>
          Enable Two-Factor Authentication (2FA)
        </span>
        <span style={{
          color: (enabled || showQR) ? "green" : "red",
          fontWeight: 500,
          marginLeft: 16,
          fontSize: 16
        }}>
          {(enabled || showQR) ? "Enabled" : "Disabled"}
        </span>
      </div>

      {showQR && (
        <div className="mt-3">
          <img src={`data:image/png;base64,${qrB64}`} alt="2FA QR code" style={{ width: 180, height: 180 }} />
          <div style={{
            background: "#fffbe6",
            border: "1px solid #ffe58f",
            color: "#ad8b00",
            padding: "12px",
            borderRadius: 8,
            margin: "18px 0",
            display: "flex",
            alignItems: "center"
          }}>
            <span style={{ fontSize: 22, marginRight: 12 }}>⚠️</span>
            <div>
              <strong>Important:</strong> Save this secret key in a safe place.<br />
              <span>
                If you lose access to your authenticator app or device, you will need this key to regain access.
              </span>
            </div>
          </div>
          <div style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",         
            marginBottom: 10,
            wordBreak: "break-all",   
            minWidth: 0
          }}>
            <b>Secret:</b>
            <code style={{
              margin: "0 12px",
              fontSize: 17,
              wordBreak: "break-all",    
              overflowWrap: "anywhere",  
              minWidth: 0
            }}>{secret}</code>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleCopySecret}
              style={{ padding: "2px 8px", fontSize: 15 }}
              type="button"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div>
            <input
              placeholder="Enter 6-digit code"
              value={otp}
              onChange={e => setOtp(e.target.value)}
              maxLength={6}
              className="form-control"
              style={{ width: 220, display: "inline", marginRight: 8 }}
            />
            <button className="btn btn-success" onClick={handleVerify2FA} disabled={pending}>
              Verify & Enable
            </button>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger mt-2">{error}</div>}
      {success && <div className="alert alert-success mt-2">{success}</div>}
    </div>
  );
};

const MyAccount = ({ onAccountDeleted }) => {
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchProfile = () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!token || !userId) {
      setError("Authentication error. Please log in again.");
      setLoading(false);
      return;
    }

    api
      .get(`/myaccount/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { user_id: userId },
      })
      .then((response) => {
        setProfile({
          username: response.data.username || "",
          email: response.data.email || "",
          phone_number: response.data.phone_number || "",
          age_group: response.data.age_group || "",
          location: response.data.location || "",
          industry: response.data.industry || "",
        });
        setFormData({
          username: response.data.username || "",
          email: response.data.email || "",
          phone_number: response.data.phone_number || "",
          age_group: response.data.age_group || "",
          location: response.data.location || "",
          industry: response.data.industry || "",
        });
        setTwoFAEnabled(!!response.data.is_2fa_enabled);
        setLoading(false);
        console.log("Profile data received:", response.data);
        console.log("2FA enabled prop from backend:", response.data.is_2fa_enabled);
      })
      .catch((error) => {
        setError("Failed to load profile information. Please try again later.");
        setLoading(false);
      });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    console.log("Token from localStorage:", token);
    console.log("User ID from localStorage:", userId);

    if (!token || !userId) {
      setError("Authentication error. Please log in again.");
      setLoading(false);
      return;
    }

    api
      .get(`${API_BASE_URL}/myaccount/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { user_id: userId },
      })
      .then((response) => {
        console.log("Profile data received:", response.data);

        setProfile({
          username: response.data.username || "",
          email: response.data.email || "",
          phone_number: response.data.phone_number || "",
          age_group: response.data.age_group || "",
          location: response.data.location || "",
          industry: response.data.industry || "",
        });

        setFormData({
          username: response.data.username || "",
          email: response.data.email || "",
          phone_number: response.data.phone_number || "",
          age_group: response.data.age_group || "",
          location: response.data.location || "",
          industry: response.data.industry || "",
        });
        setTwoFAEnabled(!!response.data.is_2fa_enabled);

        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching profile:", error.response?.data || error.message);
        setError("Failed to load profile information. Please try again later.");
        setLoading(false);
      });
  }, []);


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");
  
    if (!token || !userId) {
      setError("Authentication error. Please log in again.");
      return;
    }
  
    console.log("Updating profile with:", {
      user_id: userId,
      ...formData,
    });
  
    api
      .put(
        `${API_BASE_URL}/myaccount/update/`,
        {
          user_id: userId, // Ensure user_id is included in the request body
          ...formData,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => {
        console.log("Profile updated successfully:", response.data);
        setMessage("Profile updated successfully.");
      })
      .catch((error) => {
        console.error("Error updating profile:", error.response?.data || error.message);
        setMessage("Error updating profile.");
      });
  };

  const handleDeleteAccount = () => {
    const userId = localStorage.getItem("user_id");

    if (window.confirm("Are you sure you want to delete your account?")) {
      const token = localStorage.getItem("token");

      api
        .delete(`${API_BASE_URL}/myaccount/delete/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { user_id: userId },
        })
        .then(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user_id");
          onAccountDeleted(); // Notify parent component to handle logout
        })
        .catch((error) => {
          console.error("Error deleting account:", error);
          setError("Failed to delete account. Please try again.");
        });
    }
  };

 // if (loading) return <p>Loading...</p>;
  // if (error) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div className="container">
      <h1>My Account</h1>
      {message && <p className="alert alert-info">{message}</p>}

      {profile && (
        <>
          <div className="card p-3">
            <h3>Settings</h3>
            {/* 2FA SETTINGS CARD */}
            <TwoFactorSettings enabled={twoFAEnabled} refreshStatus={fetchProfile} />
          </div>
          <div className="card p-3 mt-4">
            <h3>Profile Information</h3>
            <label>Username:</label>
            <input
              type="text"
              name="username"
              value={formData.username || ""}
              onChange={handleChange}
              className="form-control"
            />

            <label>Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              className="form-control"
            />

            <label>Phone Number:</label>
            <input
              type="text"
              name="phone_number"
              value={formData.phone_number || ""}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="card p-3 mt-4">
            <h3>Demographics (Optional)</h3>
            <label>Age Group:</label>
            <select
              name="age_group"
              value={formData.age_group}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">Select Age Group</option>
              <option value="18-24">18-24</option>
              <option value="25-34">25-34</option>
              <option value="35-44">35-44</option>
              <option value="45+">45+</option>
            </select>

            <label>Location:</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="form-control"
            />

            <label>Industry:</label>
            <input
              type="text"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="card p-3 mt-4">
          <button onClick={handleUpdateProfile} className="btn btn-primary mt-3">
              Update Profile
            </button>
            </div>

          <div className="mt-4">
            <button className="btn btn-danger" onClick={handleDeleteAccount}>
              Delete My Account
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default MyAccount;